import { ChevronRight, Building2, CreditCard } from "lucide-react";
import { J, MANROPE, TRACK_TAG, ddMon, targetLine, dueLine, initials } from "./journeyTokens";

/* §8 — eight segments. Done, current (accent), not started. */
export const StageBar = ({ stages, currentIndex, height = 8 }) => (
  <div className="flex" style={{ gap: 3 }}>
    {stages.map((s, i) => (
      <span key={s.key || i} title={`${i + 1}. ${s.name} · ${s.completed ? "done" : i + 1 === currentIndex ? "current" : "not started"}`}
        style={{
          flex: 1, height, borderRadius: 2,
          background: s.completed ? J.stageDone : i + 1 === currentIndex ? J.accent : J.stageNot,
          boxShadow: !s.completed && i + 1 === currentIndex ? `inset 0 0 0 1px ${J.accentEdge}` : undefined,
        }} />
    ))}
  </div>
);

const Csm = ({ name }) => (
  <div className="flex items-center gap-1.5 mt-1">
    <span className="inline-flex items-center justify-center rounded-full shrink-0"
      style={{ width: 20, height: 20, fontSize: 9, fontWeight: 700, ...MANROPE,
        background: name ? "#F1EEE6" : J.overdueFill,
        border: name ? `1px solid ${J.edge}` : `1px dashed ${J.overdueEdge}`,
        color: name ? J.ink2 : J.overdueInk }}>{name ? initials(name) : "?"}</span>
    <span className="text-[12px] truncate"
      style={name ? { color: J.ink2 } : { color: J.overdueInk, fontWeight: 600 }}>{name || "No CSM"}</span>
  </div>
);

/* Only appears when Stripe says there is a live subscription on this client, so
   its absence is the "not paying" state and nothing has to shout about it. */
export const PayingTag = ({ billing, clientId }) => {
  if (!billing?.paying) return null;
  const detail = [
    billing.trialing ? "on trial" : "paying",
    billing.plans?.length ? billing.plans.join(" + ") : null,
    billing.amount_text || (billing.mrr ? `A$${(billing.mrr / 100).toFixed(0)}/mo` : null),
    billing.renews_on ? `renews ${billing.renews_on.slice(0, 10)}` : null,
    billing.ending ? "cancels at period end" : null,
  ].filter(Boolean).join(" · ");
  return (
    <span className="mono inline-flex items-center gap-1 whitespace-nowrap shrink-0"
      title={`Stripe: ${detail}`}
      style={{ fontSize: 10, fontWeight: 700, borderRadius: 4, padding: "2px 6px",
        background: "#E3F3E9", border: "1px solid #A9CFBB", color: "#0E6B3F" }}
      data-testid={`journey-paying-${clientId}`}>
      <CreditCard style={{ width: 10, height: 10 }} />
      {billing.trialing ? "TRIAL" : "PAYING"}
    </span>
  );
};

/* §7 — one row per onboarding client. The whole row opens the panel. */
export const JourneyRow = ({ r, today, open, onOpen }) => {
  const tag = TRACK_TAG[r.track] || TRACK_TAG.GUIDED;
  const t = targetLine(r.target_go_live, today, r.went_live);
  const nx = r.next_action || {};
  const barPct = Math.min(100, (r.hours_logged / (r.hours_allowance || 1)) * 100);

  return (
    <div role="button" tabIndex={0} onClick={() => onOpen(r)}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onOpen(r)}
      className="journey-row items-center cursor-pointer transition-colors hover:bg-[#FBFAF7]"
      style={{ gap: 16, padding: "14px 18px", borderTop: `1px solid ${J.rule}`,
        background: open ? "#FFFBEA" : undefined, opacity: r.is_live ? 0.7 : 1,
        boxShadow: r.past_target ? `inset 3px 0 0 ${J.overdue}` : undefined }}
      data-testid={`journey-row-${r.client_id}`}>

      <div className="jr-client min-w-0">
        <div className="flex items-baseline gap-2 min-w-0">
          <a href={`/clients/${encodeURIComponent(r.client_id)}`} onClick={(e) => e.stopPropagation()}
            title={`Open ${r.company_name}'s record`} data-testid={`journey-client-link-${r.client_id}`}
            className="text-[14px] font-semibold truncate hover:underline" style={{ color: J.ink }}>
            {r.company_name}
          </a>
          <span className="mono text-[10.5px] shrink-0" style={{ color: J.muted }}>{r.client_id}</span>
          <PayingTag billing={r.billing} clientId={r.client_id} />
        </div>
        <Csm name={r.csm} />
      </div>

      <div className="jr-track">
        <span className="mono whitespace-nowrap" style={{ fontSize: 10, fontWeight: 700, borderRadius: 4,
          padding: "2px 6px", background: tag.fill, color: tag.ink, border: `1px solid ${tag.edge}` }}>{tag.label}</span>
      </div>

      <div className="jr-stage min-w-0">
        <StageBar stages={r.stages} currentIndex={r.stage_index} />
        <div className="text-[12px] mt-1 truncate" style={{ color: J.ink2 }}>
          {r.stage_index}. {r.stage_name} · <span className="mono">{r.stages_completed}/{r.stages_total}</span> done
        </div>
      </div>

      <div className="jr-target">
        <div className="mono text-[12.5px] font-bold" style={{ color: r.target_go_live ? J.ink : J.muted }}>
          {r.went_live ? ddMon(r.went_live) : ddMon(r.target_go_live) || "Not set"}
        </div>
        <div className="text-[11.5px]" style={{ color: t.color, fontWeight: t.bold ? 600 : 400 }}>{t.text}</div>
      </div>

      <div className="jr-hours">
        <div className="mono text-[12.5px]" style={{ color: J.ink }}>
          <span style={{ fontWeight: 700 }}>{r.hours_logged}</span>
          <span style={{ color: J.muted }}>{r.hours_allowance ? ` / ${r.hours_allowance}h` : "h · no cap"}</span>
        </div>
        <div className="mt-1" style={{ width: 72, height: 4, borderRadius: 2, background: J.stageNot }}>
          <div style={{ width: `${barPct}%`, height: 4, borderRadius: 2,
            background: r.hours_over ? J.overdue : "#8A857A" }} />
        </div>
      </div>

      <div className="jr-training flex items-center gap-1 flex-wrap">
        {((r.training || {}).sessions || []).map((s) => (
          <span key={s.key}
            title={`${s.title || s.label}${s.state === "done"
              ? ` · delivered ${ddMon(s.completed_date) || "date not set"}`
              : s.due_date ? ` · due ${ddMon(s.due_date)}` : " · not scheduled"}`}
            className="mono whitespace-nowrap"
            style={{ fontSize: 9.5, fontWeight: 700, borderRadius: 3, padding: "1px 4px",
              background: s.state === "done" ? J.okFill : "#fff",
              color: s.state === "done" ? J.okInk : "#A8A399",
              border: `1px solid ${s.state === "done" ? J.okEdge : J.edge}` }}>{s.label}</span>
        ))}
      </div>

      <div className="jr-next min-w-0">
        {r.is_live ? (
          <span className="text-[13px]" style={{ color: J.muted }}>Journey complete</span>
        ) : nx.title ? (
          <>
            <div className="text-[13px] font-semibold truncate" style={{ color: J.ink }}>{nx.title}</div>
            <div className="text-[11.5px] truncate">
              {(() => { const d = dueLine(nx.due_date, today); return (
                <span className="mono" style={{ color: d.color, fontWeight: d.bold ? 600 : 400 }}>{d.text}</span>
              ); })()}
              <span style={{ color: J.muted }}> · {r.csm ? `CSM ${r.csm}` : "CSM · unassigned"}</span>
            </div>
          </>
        ) : <span className="text-[13px]" style={{ color: J.muted }}>Nothing outstanding</span>}
      </div>

      <ChevronRight className="jr-chev w-4 h-4 shrink-0" style={{ color: J.muted }} />
    </div>
  );
};

export const ClientIcon = Building2;
